import { useRef, useState } from 'react';
import type { SessionFile } from '../useConverter';

interface Props {
  files: SessionFile[];
  activeId: string | null;
  onFiles: (items: { name: string; bytes: ArrayBuffer }[]) => void;
  onSelect: (id: string) => void;
  onRemove: (id: string) => void;
  onDemo: () => void;
}

export function FileDrop({ files, activeId, onFiles, onSelect, onRemove, onDemo }: Props) {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);

  const read = async (list: FileList | null) => {
    if (!list?.length) return;
    const items = await Promise.all(
      Array.from(list).map(async (f) => ({ name: f.name, bytes: await f.arrayBuffer() })),
    );
    onFiles(items);
  };

  return (
    <section aria-labelledby="input-heading" className="flex flex-col gap-3">
      <h2 id="input-heading" className="legend text-[13px]">
        Input
      </h2>
      <div
        role="button"
        tabIndex={0}
        aria-label="Drop SID files here or press Enter to choose files"
        onClick={() => input.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            input.current?.click();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          void read(e.dataTransfer.files);
        }}
        className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed px-4 py-6 text-center text-[12px] transition-colors ${
          over ? 'border-accent bg-panel2' : 'border-line hover:border-accent'
        }`}
      >
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          className="mb-2 h-7 w-7 text-accent"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <path
            d="M12 16V4m0 0-4 4m4-4 4 4M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span className="font-medium">Drop .sid files here</span>
        <span className="text-[11px] text-muted">or click to browse (PSID / RSID)</span>
      </div>
      <input
        ref={input}
        type="file"
        accept=".sid,.psid,.rsid"
        multiple
        className="hidden"
        data-testid="file-input"
        onChange={(e) => {
          void read(e.target.files);
          e.target.value = '';
        }}
      />
      <button type="button" className="btn" onClick={onDemo}>
        Try the built-in demo tune
      </button>

      {files.length > 0 && (
        <ul aria-label="Loaded files" className="flex flex-col gap-1">
          {files.map((f) => (
            <li key={f.id} className="flex items-stretch gap-1">
              <button
                type="button"
                onClick={() => onSelect(f.id)}
                aria-current={f.id === activeId ? 'true' : undefined}
                className={`min-w-0 flex-1 rounded-md px-2 py-1.5 text-left text-[12px] ${
                  f.id === activeId
                    ? 'border border-accent bg-panel2'
                    : 'border border-transparent hover:border-line hover:bg-panel2'
                }`}
              >
                <span className="block truncate font-medium">{f.header?.name || f.name}</span>
                <span className={`block truncate text-xs ${f.error ? 'text-danger' : 'text-muted'}`}>
                  {f.error ? 'Could not load' : f.header ? f.header.author || 'Unknown author' : 'Loading…'}
                </span>
              </button>
              <button
                type="button"
                className="rounded-md px-2 text-muted hover:text-accent"
                aria-label={`Remove ${f.header?.name || f.name}`}
                onClick={() => onRemove(f.id)}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
